import {useNavigate, useParams} from 'react-router-dom';

import {
    Anchor,
    Group,
    Stack,
    Text,
    Title,
} from '@mantine/core';

import {
    FlexiCancelButton,
    FormPageLayout,
    PlatformLoader,
    SimplePageContentWrapper,
} from '@flexinet/ui-components';

import {EditPageNavigation} from '../navigation/EditPageNavigation';
import {useExecution} from '../hooks/useExecution.ts';
import {useOnExecutionProgress} from '../hooks/useOnExecutionProgress.ts';
import {CancelExecutionButton} from '../buttons/CancelExecutionButton.tsx';
import {ExecutionStatusBadge} from '../components/ExecutionStatusBadge.tsx';
import {PhaseBlock} from '../components/PhaseBlock.tsx';
import {useVerifiedPermissions} from '@flexinet/auth-components';
import {Execution} from '../types.ts';
import {formatTimestamp} from '../../../utils/TableTimestamps.tsx';

const SFN_REGION = 'us-west-2';

const sfnConsoleUrl = (arn: string) =>
    `https://console.aws.amazon.com/states/home?region=${SFN_REGION}#/v2/executions/details/${encodeURIComponent(arn)}`;

const ExecutionHeader = ({execution}: { execution: Execution }) => {
    return (
        <Stack gap={'xs'}>
            <Group gap={'sm'} wrap={'wrap'}>
                <Title order={3}>{execution.name ?? execution.id}</Title>
                <ExecutionStatusBadge status={execution.executionStatus}/>
            </Group>

            {execution.description ? (
                <Text size={'sm'} c={'dimmed'}>{execution.description}</Text>
            ) : null}

            <Group gap={'lg'} wrap={'wrap'}>
                <Text size={'sm'}>
                    <Text span fw={600}>Started:</Text>{' '}
                    {execution.startedAt ? formatTimestamp(execution.startedAt) : 'n/a'}
                </Text>
                <Text size={'sm'}>
                    <Text span fw={600}>Completed:</Text>{' '}
                    {execution.completedAt ? formatTimestamp(execution.completedAt) : 'n/a'}
                </Text>
                <Text size={'sm'}>
                    <Text span fw={600}>Progress:</Text>{' '}
                    {execution.completedPhases ?? 0} / {execution.totalPhases ?? 0}
                </Text>
                <Text size={'sm'}>
                    <Text span fw={600}>Started by:</Text>{' '}
                    {execution.createdByUsername ?? 'n/a'}
                </Text>
            </Group>

            {execution.sfnExecutionArn ? (
                <Text size={'sm'}>
                    <Text span fw={600}>Step Functions:</Text>{' '}
                    <Anchor
                        href={sfnConsoleUrl(execution.sfnExecutionArn)}
                        target={'_blank'}
                        rel={'noopener noreferrer'}
                        size={'sm'}
                    >
                        Open in AWS console
                    </Anchor>
                </Text>
            ) : null}

            {execution.failureReason || execution.failedStepName ? (
                <Stack gap={4}>
                    {execution.failedStepName ? (
                        <Text size={'sm'} c={'red'}>
                            <Text span fw={600}>Failed step:</Text> {execution.failedStepName}
                        </Text>
                    ) : null}
                    {execution.failureReason ? (
                        <Text size={'sm'} c={'red'}>
                            <Text span fw={600}>Failure reason:</Text> {execution.failureReason}
                        </Text>
                    ) : null}
                </Stack>
            ) : null}
        </Stack>
    );
};

export const EditPage = () => {

    const {id: executionId} = useParams();

    if (typeof executionId === 'undefined') {
        throw new Error('executionId is required parameter');
    }

    const navigate = useNavigate();

    const {allowedActions} = useVerifiedPermissions();

    const {record: execution} = useExecution(executionId);

    useOnExecutionProgress(executionId);

    if (!execution) {
        return <PlatformLoader message={'Loading execution...Please wait'}/>;
    }

    const phases = (execution.phases ?? []).slice().sort(
        (a, b) => (a.position ?? 0) - (b.position ?? 0)
    );

    const tools = [];
    if (allowedActions.includes('CancelExecution')) {
        tools.push(<CancelExecutionButton key={'cancel-execution'} execution={execution}/>);
    }
    tools.push(<FlexiCancelButton key={'close'} onClick={() => navigate('/executions')}/>);

    return (
        <FormPageLayout
            title={execution.name ?? execution.id}
            pageNavigation={<EditPageNavigation component={execution}/>}
            tools={tools}
        >
            <SimplePageContentWrapper>
                <Stack gap={'xl'}>
                    <ExecutionHeader execution={execution}/>

                    <Stack gap={'lg'}>
                        <Title order={4}>Phases</Title>
                        {phases.length === 0 ? (
                            <Text size={'sm'} c={'dimmed'}>No phases recorded for this execution yet.</Text>
                        ) : (
                            phases.map((phase) => <PhaseBlock key={phase.id} phase={phase}/>)
                        )}
                    </Stack>
                </Stack>
            </SimplePageContentWrapper>
        </FormPageLayout>
    );
};
